/*
===============================================================================================================
                                 Copyright 2022-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, {
  useContext, useEffect, useState, forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import { DateRangePicker } from 'react-dates';
import { useMediaQuery } from 'react-responsive';
import moment from 'moment';
import ErrorReportingHOC from '@/components/error-reporting-hoc';
import { LocaleContext } from '@/contexts/locale-context';
import TextDiv from '@/components/text-div';
import CalenderIcon from '@/public/icons/calendar.svg';
import CloseIcon from '@/public/icons/close.svg';
import 'moment/min/locales.min';
import { getEndOfDayUTC, getStartOfDayUTC, formatDateForBackend } from '@/utilities/dates';

/**
 * RangePicker contains title, two date inputs, calendar popup, error message.
 * Is wrapped in the ErrorReporting HOC.
 * @param {string} inputSelectorId - DOM id, used to identify this element
 * @param {string} title - Displayed above the text field
 * @param {number} inputHeight - CSS height in pixels. Defaults to 42px
 * @param {number} inputWidth - CSS Percentage width. Defaults to 100
 * @param {boolean} required - Whether or not this is a required field
 * @param {Object} onChange - On Change function call.
 * @param {boolean} showError - Whether or not to render error container
 * @param {Object|string} defaultStartDate - Start date value on component load. Should be either Momentjs object or UTC string
 * @param {Object|string} defaultEndDate - End date value on component load. Should be either Momentjs object or UTC string
 */
const RangePicker = forwardRef(({
  inputSelectorId,
  title,
  inputHeight,
  inputWidth,
  required,
  onChange,
  onRangeChange,
  defaultStartDate,
  defaultEndDate,
  showError,
  setContent, // Passed in automatically, no need to set
  error, // Passed in automatically, no need to set
}, ref) => {
  const {
    currentLocale, textDirection, dateFormat,
    translate, getIntlLocale,
  } = useContext(LocaleContext);
  const intlLocale = getIntlLocale();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  const startDateDefault = defaultStartDate?.startDate;
  const endDateDefault = defaultEndDate?.endDate;
  const momentStartDate = moment.utc(startDateDefault);
  const momentEndDate = moment.utc(endDateDefault);
  // the start of today's date
  const pastTime = moment.utc().startOf('day').toISOString();

  const handleDatesRangeChange = (newDates) => {
    let formattedDate = {};
    // a date in the past is sent to represent a cleared maintenance window if date range is cleared
    if ((newDates.startDate === null) && (newDates.endDate === null)) {
      formattedDate = {
        start: pastTime,
        end: pastTime,
      };
    }

    if (newDates.startDate) {
      formattedDate = {
        ...formattedDate,
        start: newDates.startDate ? getStartOfDayUTC(newDates.startDate) : null,
      };
    }

    if (newDates.endDate) {
      formattedDate = {
        ...formattedDate,
        end: newDates.endDate ? getEndOfDayUTC(newDates.endDate) : null,
      };
    }

    onRangeChange(formattedDate || null);
  };

  const handleDatesChange = (newDates) => {
    onChange(newDates);
    handleDatesRangeChange(newDates);
    setContent(formatDateForBackend(newDates.endDate));
    setStartDate(newDates?.startDate);
    setEndDate(newDates?.endDate);
  };

  const onFocusChange = (prop) => {
    setFocusedInput(prop);
  };

  if (!startDate && momentStartDate.isValid()) {
    setStartDate(momentStartDate);
  }

  if (!endDate && momentEndDate.isValid()) {
    setEndDate(momentEndDate);
  }

  useEffect(() => {
    if (startDateDefault === null) {
      setStartDate(startDateDefault);
    }
    if (endDateDefault === null) {
      setEndDate(endDateDefault);
    }
  }, [startDateDefault, endDateDefault]);

  useEffect(() => {
    if (!intlLocale) {
      setStartDate(null);
      setEndDate(null);
    }
  }, [currentLocale]);

  return (
    <>
      <div className="date-picker-input" id={inputSelectorId}>
        {title && (
          <div className="title">
            <TextDiv>
              {`${title}${required === true ? '*' : ''}`}
            </TextDiv>
          </div>
        )}
        <div>
          <div id="date-range-picker" className={`date-picker-input-box${error ? ' errored' : ''}${textDirection === 'rtl' ? ' rtl' : ''}`}>
            <DateRangePicker
              displayFormat={intlLocale
                ? moment.localeData().longDateFormat('L')
                : dateFormat?.format}
              startDate={startDate}
              startDateId="date-range-picker-start"
              startDatePlaceholderText={translate('constants.startDatePlaceholder')}
              endDate={endDate}
              endDateId="date-range-picker-end"
              endDatePlaceholderText={translate('constants.endDatePlaceholder')}
              onDatesChange={handleDatesChange}
              focusedInput={focusedInput}
              onFocusChange={onFocusChange}
              isRTL={textDirection === 'rtl'}
              inputIconPosition="after"
              customArrowIcon={<span>-</span>}
              customCloseIcon={<CloseIcon />}
              customInputIcon={<CalenderIcon />}
              orientation={isMobile ? 'vertical' : 'horizontal'}
              verticalHeight={600}
              verticalSpacing={0}
              isOutsideRange={() => false}
              hideKeyboardShortcutsPanel
              showDefaultInputIcon
              showClearDates
              noBorder
            />
          </div>
          {showError && (
            <div className={`error ${error ? ' visible' : ''}`} ref={ref}>
              {error && <TextDiv>{error}</TextDiv>}
            </div>
          )}
        </div>
        <style jsx>
          {`
            .date-picker-input {
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              width: ${inputWidth}%;
            }

            @media only screen and (max-width: 768px) {
              .date-picker-input {
                width: 100%;
              }
              .error.visible {
                font-size: var(--font-size-error);
              }
            }

            @media only screen and (min-width: 768px) {
              .error.visible {
                font-size: var(--font-size-error);
              }
            }

            .date-picker-input-box {
              align-items: center;
              background: var(--color-white);
              border-radius: 3px;
              border: 1px solid var(--color-mid-grey);
              color: var(--color-primary-dark);
              display: flex;
              font-size: var(--font-size-body);
              font-weight: var(--font-weight-body);
              height: ${inputHeight}px;
              padding: 2px;
              position: relative;
              width: 100%;
            }

            .date-picker-input-box.errored {
              border: 1px solid var(--color-error-red);
              font-size: var(--font-size-error);
            }

            .error {
              min-height: 20px;
              margin-top: 3px;
              visibility: hidden;
              white-space: break-spaces;
            }

            .error.visible {
              color: var(--color-error-red);
              font-size: var(--font-size-error);
              visibility: visible;
            }

            .title {
              color: var(--color-almost-black);
              display: block;
              font-size: var(--font-size-H2);
              font-weight: var(--font-weight-H2);
              line-height: 2;
            }
        `}
        </style>
      </div>
    </>
  );
});

RangePicker.defaultProps = {
  title: null,
  inputHeight: 42,
  inputWidth: 100,
  required: false,
  onChange: () => null,
  onRangeChange: () => null,
  defaultStartDate: null,
  defaultEndDate: null,
  showError: true,
  error: null,
};

RangePicker.propTypes = {
  inputSelectorId: PropTypes.string.isRequired,
  title: PropTypes.string,
  inputHeight: PropTypes.number,
  inputWidth: PropTypes.number,
  required: PropTypes.bool,
  showError: PropTypes.bool,
  error: PropTypes.string,
  onChange: PropTypes.func,
  onRangeChange: PropTypes.func,
  setContent: PropTypes.func.isRequired,
  defaultStartDate: PropTypes.oneOfType([PropTypes.instanceOf(moment), PropTypes.string, PropTypes.object]),
  defaultEndDate: PropTypes.oneOfType([PropTypes.instanceOf(moment), PropTypes.string, PropTypes.object]),
};

export default ErrorReportingHOC(RangePicker);
